import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { session as sessionStorage } from 'lib/storage';
import { SESSION_STORAGE } from 'lib/storage/sessionStorage';

import type { FeatureFlagsContextType } from '.';
import { FeatureFlagsContext } from '.';

interface Props {
  children: ReactNode;
}

export const FeatureFlagsProvider = ({ children }: Props) => {
  const client = useLDClient();
  const flags = useFlags();

  const value = useMemo(() => {
    const forcedFlag = sessionStorage.getItem(SESSION_STORAGE.forcedFlag);
    const forcedFlagObj = forcedFlag ? JSON.parse(forcedFlag) : {};
    // avoid spreading all flags as this logs them as being still all used
    const proxiedFlags = new Proxy<typeof flags>(flags, {
      get(allFlags, prop, receiver) {
        const actual = Reflect.get(allFlags, prop, receiver);
        if (
          (
            Object.keys(forcedFlagObj) as (keyof typeof forcedFlagObj)[]
          ).includes(prop)
        ) {
          return forcedFlagObj[prop];
        }
        return actual;
      },
    });

    const allFlags = client?.allFlags() ?? {};

    const trackedFlags = new Set(allFlags['monitored-feature-flags'] ?? []);

    const monitoredFlags = Object.fromEntries(
      Object.entries(allFlags).filter(([key]) => trackedFlags.has(key))
    );

    const identify: FeatureFlagsContextType['identify'] = !client
      ? undefined
      : async (
          { custom = {}, privateAttributeNames, ...user },
          hash?,
          onDone?
        ) =>
          client.identify(
            {
              kind: 'user',
              ...user,
              ...custom,
              _meta: {
                privateAttributes: privateAttributeNames,
              },
            },
            hash,
            onDone
          );

    return {
      flags: proxiedFlags,
      monitoredFlags,
      identify,
    };
  }, [client, flags]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
